import React, { useState, useEffect, useRef } from 'react';

const SubtitleComponent = ({
    projectId,
    currentTime,
    settings = {},
    subtitles = [],
    videoRef
}) => {
    const [currentSubtitles, setCurrentSubtitles] = useState([]);
    const containerRef = useRef(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
    const [videoSize, setVideoSize] = useState({ width: 0, height: 0 });
    const [layoutRatio, setLayoutRatio] = useState(1);

    // Ajouter cet useEffect juste après les autres useEffect du composant
    useEffect(() => {
        // Force la mise à jour des sous-titres actuels quand les settings changent
        if (currentSubtitles.length > 0 && settings) {
            // Créer de nouveaux objets pour provoquer le re-rendu
            setCurrentSubtitles(prev => prev.map(subtitle => ({ ...subtitle })));
        }
    }, [settings]); // Dépendance uniquement sur settings

    // Convertir couleur ASS en rgba pour CSS - FONCTION CORRIGÉE
    function assColorToRgba(assColor) {
        // Format attendu: &HAABBGGRR
        if (!assColor || typeof assColor !== 'string' || !assColor.startsWith('&H')) {
            console.log('Format de couleur invalide:', assColor);
            return 'rgba(255, 255, 255, 1)'; // Valeur par défaut
        }

        try {
            // Extraire les composantes
            const colorValue = assColor.substring(2).padStart(8, '0');

            // S'assurer que la chaîne a la bonne longueur
            if (colorValue.length !== 8) {
                console.log('Longueur de couleur incorrecte:', colorValue);
                return 'rgba(255, 255, 255, 1)';
            }

            const alphaHex = colorValue.substring(0, 2);
            const blueHex = colorValue.substring(2, 4);
            const greenHex = colorValue.substring(4, 6);
            const redHex = colorValue.substring(6, 8);

            // Convertir en décimal avec des vérifications
            const alpha = 1 - (parseInt(alphaHex, 16) / 255);
            const red = parseInt(redHex, 16);
            const green = parseInt(greenHex, 16);
            const blue = parseInt(blueHex, 16);

            // Vérifier que les valeurs sont valides
            if (isNaN(red) || isNaN(green) || isNaN(blue) || isNaN(alpha)) {
                console.log('Valeurs de couleur invalides:', { red, green, blue, alpha });
                return 'rgba(255, 255, 255, 1)';
            }

            return `rgba(${red}, ${green}, ${blue}, ${alpha.toFixed(2)})`;
        } catch (error) {
            console.error('Erreur lors de la conversion de couleur ASS:', error, assColor);
            return 'rgba(255, 255, 255, 1)';
        }
    }

    // Observer les dimensions du conteneur et de la vidéo
    useEffect(() => {
        if (!containerRef.current || !videoRef.current) return;

        const updateSizes = () => {
            if (containerRef.current) {
                // Dimensions du conteneur
                const containerWidth = containerRef.current.clientWidth;
                const containerHeight = containerRef.current.clientHeight;
                setContainerSize({ width: containerWidth, height: containerHeight });

                // Dimensions réelles de la vidéo (si chargée)
                if (videoRef.current) {
                    const videoWidth = videoRef.current.videoWidth || containerWidth;
                    const videoHeight = videoRef.current.videoHeight || containerHeight;
                    setVideoSize({ width: videoWidth, height: videoHeight });

                    // Calculer le ratio de mise à l'échelle
                    const containerRatio = containerWidth / containerHeight;
                    const videoRatio = videoWidth / videoHeight;

                    // Déterminer comment la vidéo s'adapte au conteneur
                    let scale = 1;
                    if (containerRatio > videoRatio) {
                        // La vidéo est limitée par la hauteur
                        scale = containerHeight / videoHeight;
                    } else {
                        // La vidéo est limitée par la largeur
                        scale = containerWidth / videoWidth;
                    }

                    setLayoutRatio(scale);
                }
            }
        };

        // Créer un ResizeObserver pour surveiller les changements de taille
        const resizeObserver = new ResizeObserver(updateSizes);
        resizeObserver.observe(containerRef.current);

        // Mettre à jour les dimensions initiales
        updateSizes();

        // Un écouteur supplémentaire pour les changements de la vidéo elle-même
        const handleVideoLoaded = () => {
            updateSizes();
        };

        videoRef.current.addEventListener('loadedmetadata', handleVideoLoaded);

        return () => {
            resizeObserver.disconnect();
            if (videoRef.current) {
                videoRef.current.removeEventListener('loadedmetadata', handleVideoLoaded);
            }
        };
    }, [videoRef]);

    // Trouver tous les sous-titres actifs à afficher
    useEffect(() => {
        if (!settings.enabled) {
            setCurrentSubtitles([]);
            return;
        }

        // Trouver les sous-titres actifs
        const activeSubtitles = subtitles.filter(
            subtitle => currentTime >= subtitle.start && currentTime < subtitle.end
        );

        if (activeSubtitles.length === 0) {
            setCurrentSubtitles([]);
            return;
        }

        // Traiter les sous-titres actifs - gère les chevauchements selon le paramètre
        let processedSubtitles;
        if (settings.overlapHandling === 'advanced') {
            // Gérer les chevauchements avec les différentes positions
            processedSubtitles = processSubtitlesWithOverlap(activeSubtitles, settings);
        } else {
            // Mode simple - tous les sous-titres au même endroit
            processedSubtitles = activeSubtitles.map(subtitle => ({
                ...subtitle,
                styleLevel: 'main'
            }));
        }

        setCurrentSubtitles(processedSubtitles);
    }, [currentTime, subtitles, settings]);

    // Détecte les chevauchements et attribue des styles - format ASS interne
    function processSubtitlesWithOverlap(subtitles, settings) {
        if (!subtitles || subtitles.length === 0) {
            return [];
        }

        // Trier par temps de début
        const sortedSubtitles = [...subtitles].sort((a, b) => a.start - b.start);

        // Structure pour suivre les lignes occupées
        const occupiedLines = {
            main: [],     // ligne principale
            bottom2: [],  // ligne légèrement au-dessus
            bottom3: []   // ligne encore plus haut
        };

        // Attribuer un style à chaque sous-titre
        const processedSubtitles = sortedSubtitles.map(subtitle => {
            // Vérifier la disponibilité de chaque position
            const positions = ['main', 'bottom2', 'bottom3'];

            // Trouver la première position disponible
            let selectedPosition = null;

            for (const pos of positions) {
                const isOccupied = occupiedLines[pos].some(interval => {
                    return (subtitle.start < interval.end && subtitle.end > interval.start);
                });

                if (!isOccupied) {
                    selectedPosition = pos;
                    break;
                }
            }

            // Si toutes les positions sont occupées, utiliser quand même la dernière
            if (selectedPosition === null) {
                selectedPosition = positions[positions.length - 1];
                console.log(`Avertissement: Plus de trois sous-titres se chevauchent à ${subtitle.start}s`);
            }

            // Marquer cette position comme occupée pendant la durée du sous-titre
            occupiedLines[selectedPosition].push({
                start: subtitle.start,
                end: subtitle.end
            });

            // Retourner le sous-titre avec son niveau de style
            return {
                ...subtitle,
                styleLevel: selectedPosition
            };
        });

        return processedSubtitles;
    }

    // Adaptez un style ASS en style CSS - FONCTION CORRIGÉE
    function adaptAssStyleToReact(subtitle, scaleFactor) {
        // Vérifier si settings existe et assigner des valeurs par défaut si non
        const safeSettings = {
            marginV: 10,
            primaryColor: '&H00FFFFFF', // Blanc
            backColor: '&H80000000',    // Fond noir semi-transparent
            fontName: 'Arial',
            fontSize: 24,
            bold: -1,
            italic: 0,
            alignment: 2,
            borderStyle: 4,
            outline: 1,
            shadow: 1,
            marginL: 10,
            marginR: 10,
            showBackground: true,
            ...(settings || {})
        };

        // Utiliser le styleLevel pour déterminer les marges verticales
        const marginV = subtitle.styleLevel === 'main'
            ? safeSettings.marginV
            : subtitle.styleLevel === 'bottom2'
                ? safeSettings.marginV + 40
                : safeSettings.marginV + 80;

        // Utiliser prioritairement les styles personnalisés du sous-titre
        // CORRECTION: S'assurer que les couleurs ont un format valide avant de les utiliser
        const primaryColor = (subtitle.customPrimaryColor && subtitle.customPrimaryColor.startsWith('&H'))
            ? subtitle.customPrimaryColor
            : safeSettings.primaryColor;

        const backgroundColor = safeSettings.showBackground
            ? ((subtitle.customBackColor && subtitle.customBackColor.startsWith('&H'))
                ? subtitle.customBackColor
                : safeSettings.backColor)
            : '&H00000000'; // Transparent

        // S'assurer que les valeurs booléennes sont correctement interprétées
        const isBold = subtitle.bold !== undefined
            ? subtitle.bold === -1
            : safeSettings.bold === -1;

        const isItalic = subtitle.italic !== undefined
            ? subtitle.italic === -1
            : safeSettings.italic === -1;

        // Utiliser l'alignement personnalisé du sous-titre s'il existe
        const alignment = subtitle.customAlignment || safeSettings.alignment || 2;

        // Calculer la taille de police adaptée
        const fontSize = Math.max(12, (safeSettings.fontSize * scaleFactor));

        // Obtenir les positions en fonction de l'alignement (1-9 au format ASS)
        let positionStyle = {};

        // Alignement vertical (1-3: bas, 4-6: milieu, 7-9: haut)
        if (alignment >= 7) {
            positionStyle.top = `${marginV}px`;
            positionStyle.bottom = 'auto';
        } else if (alignment >= 4) {
            positionStyle.top = '50%';
            positionStyle.transform = 'translateY(-50%)';
        } else {
            positionStyle.bottom = `${marginV}px`;
            positionStyle.top = 'auto';
        }

        // Alignement horizontal (1,4,7: gauche, 2,5,8: centre, 3,6,9: droite)
        if (alignment === 1 || alignment === 4 || alignment === 7) {
            positionStyle.left = `${safeSettings.marginL}px`;
            positionStyle.right = 'auto';
            positionStyle.textAlign = 'left';
        } else if (alignment === 3 || alignment === 6 || alignment === 9) {
            positionStyle.right = `${safeSettings.marginR}px`;
            positionStyle.left = 'auto';
            positionStyle.textAlign = 'right';
        } else {
            positionStyle.left = '50%';
            positionStyle.right = 'auto';
            if (positionStyle.transform) {
                positionStyle.transform = 'translate(-50%, -50%)';
            } else {
                positionStyle.transform = 'translateX(-50%)';
            }
            positionStyle.textAlign = 'center';
        }

        // CORRECTION: Convertir les couleurs ASS en RGBA avec gestion des erreurs
        const textColor = assColorToRgba(primaryColor);
        const bgColor = safeSettings.borderStyle === 4 && safeSettings.showBackground
            ? assColorToRgba(backgroundColor)
            : 'transparent';

        // Déterminer le padding en fonction de la taille de police
        const padding = safeSettings.borderStyle === 4 && safeSettings.showBackground
            ? `${Math.max(4, fontSize * 0.15)}px ${Math.max(8, fontSize * 0.3)}px`
            : '0';

        // CORRECTION: Déterminer l'outline en fonction de borderStyle et outline
        // Si outline est à 0, on ne doit pas appliquer de contour, quelle que soit la valeur de borderStyle
        const outlineWidth = safeSettings.outline > 0 ? Math.max(1, safeSettings.outline) : 0;
        const outlineStyle = outlineWidth > 0 ? `${outlineWidth}px black` : 'none';

        // Construire les styles CSS
        return {
            position: 'absolute',
            ...positionStyle,
            color: textColor,
            fontSize: `${fontSize}px`,
            fontFamily: safeSettings.fontName || 'Arial',
            backgroundColor: bgColor,
            padding: padding,
            zIndex: 10,
            maxWidth: '80%',
            wordWrap: 'break-word',
            lineHeight: '1.2',
            borderRadius: '3px',
            whiteSpace: 'pre-line',
            fontWeight: isBold ? 'bold' : 'normal',
            fontStyle: isItalic ? 'italic' : 'normal',
            textShadow: safeSettings.shadow > 0
                ? `0px ${safeSettings.shadow}px ${safeSettings.shadow * 2}px rgba(0,0,0,0.8)`
                : 'none',
            // CORRECTION: N'appliquer le contour que si outline > 0
            textStroke: outlineWidth > 0 ? outlineStyle : 'none',
            WebkitTextStroke: outlineWidth > 0 ? outlineStyle : 'none',
        };
    }

    // Rendu des sous-titres
    const renderSubtitles = () => {
        if (currentSubtitles.length === 0) return null;

        // Calculer le facteur d'échelle pour adapter la taille des sous-titres à la taille du conteneur
        let scaleFactor = 1;

        // Taille de référence pour 720p
        if (containerSize.height > 0) {
            scaleFactor = containerSize.height / 720;
        }

        return currentSubtitles.map((subtitle, index) => {
            // Adapter le style en CSS avec mise à l'échelle
            const subtitleStyle = adaptAssStyleToReact(subtitle, scaleFactor);

            // Ajouter cette ligne pour créer une clé qui change quand outline change
            const uniqueKey = `subtitle-${index}-${subtitle.start}-outline${settings.outline || 0}`;

            return (
                <div
                    // Modifier cette ligne pour utiliser la nouvelle clé dynamique
                    key={uniqueKey}
                    style={subtitleStyle}
                >
                    {subtitle.text}
                </div>
            );
        });
    };

    // Si les sous-titres sont désactivés, ne rien afficher
    if (!settings.enabled) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                overflow: 'hidden',
                zIndex: 5
            }}
        >
            {renderSubtitles()}
        </div>
    );
};

export default SubtitleComponent;